import React, { useState, useEffect, useRef } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import { realtimeEvents, EVENT_TYPES } from '../../services/realtimeEventSystem';
import { mockWebSocket } from '../../services/mockWebSocketBridge';
import { 
  watchForEscalations,
  getEscalationNotificationCount,
  getAllActiveEscalations,
  markEscalationAsRead,
} from '../../services/escalationService';

import { API_BASE } from '../../config/runtime';

// Helper function to get time ago
const getTimeAgo = (dateString) => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  } catch {
    return 'Unknown';
  }
};

// Helper function to get status config
const getStatusConfig = (status) => {
  const configs = {
    active: {
      label: 'Active',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700',
      dotColor: 'bg-emerald-500',
    },
    waiting: {
      label: 'Waiting',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
      dotColor: 'bg-amber-500',
    },
    closed: {
      label: 'Closed',
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-600',
      dotColor: 'bg-gray-400',
    },
  };
  return configs[status] || configs.closed;
};

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M23 12l-20.89-9.08A2 2 0 0 0 1 4.81v4.3a2 2 0 0 0 1.35 1.82L16 12 2.35 13.87A2 2 0 0 0 1 15.69v4.3a2 2 0 0 0 2.11 2.06l20.89-9.08a2 2 0 0 0 0-3.78z" />
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M18 6l-12 12M6 6l12 12" />
  </svg>
);

const MoreVertIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

/* ─── Main Component ──────────────────────────────────────────────────────── */
const LiveSupport = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unreadEscalations, setUnreadEscalations] = useState([]);
  const [escalationNotificationBadge, setEscalationNotificationBadge] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const escalationWatcherRef = useRef(null);
  const menuRef = useRef(null);

  // Fetch conversations from backend
  const fetchConversations = async () => {
    console.log('[LiveSupport] Fetching conversations...');
    
    try {
      const response = await fetch(`${API_BASE}/live-support/tickets.php`);
      const data = await response.json();

      console.log('[LiveSupport] Conversations API response:', data);

      if (data.success && data.conversations) {
        console.log('[LiveSupport] Found ' + data.conversations.length + ' conversations');
        setConversations(data.conversations);
        
        // If no conversation is selected but we have conversations, select the first one
        if (!selectedConversation && data.conversations.length > 0) {
          console.log('[LiveSupport] Auto-selecting first conversation:', data.conversations[0].id);
          setSelectedConversation(data.conversations[0]);
          fetchMessages(data.conversations[0].id);
        }

        setError('');
      } else {
        console.warn('[LiveSupport] No conversations found');
        setConversations([]);
      }
    } catch (err) {
      console.error('[LiveSupport] Error fetching conversations:', err);
      setError('Failed to load conversations');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch messages for selected conversation
  const fetchMessages = async (ticketId) => {
    if (!ticketId) {
      console.log('[LiveSupport] fetchMessages called with no ticket ID');
      setMessages([]);
      return;
    }

    console.log('[LiveSupport] Fetching messages for ticket:', ticketId);

    try {
      console.log('[LiveSupport] API URL: ' + `${API_BASE}/live-support/messages.php?ticket_id=${ticketId}`);
      
      const response = await fetch(`${API_BASE}/live-support/messages.php?ticket_id=${ticketId}`);
      
      console.log('[LiveSupport] API response status:', response.status, response.statusText);
      
      const data = await response.json();

      console.log('[LiveSupport] Messages API response:', data);

      if (data.success && data.messages) {
        console.log('[LiveSupport] Message count from API:', data.messages.length);
        
        // Format messages for display
        const formattedMessages = data.messages.map((msg) => ({
          id: msg.id,
          conversationId: msg.conversationId,
          sender: msg.sender,
          senderName: msg.senderName || 'Unknown',
          message: msg.message,
          timestamp: new Date(msg.timestamp),
        }));
        console.log('[LiveSupport] Formatted ' + formattedMessages.length + ' messages for display');
        console.log('[LiveSupport] First message sample:', formattedMessages[0]);
        setMessages(formattedMessages);
      } else {
        console.warn('[LiveSupport] API returned unsuccessful response:', data);
        console.warn('[LiveSupport] Success:', data.success, 'Messages:', data.messages);
        setMessages([]);
      }
    } catch (err) {
      console.error('[LiveSupport] Error fetching messages:', err);
      console.error('[LiveSupport] Error details:', {
        message: err.message,
        stack: err.stack
      });
      setMessages([]);
    }
  };

  // Auto-scroll to latest message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Watch for real-time escalations from chatbot
  useEffect(() => {
    // Listen for connection status
    const unsubConnection = realtimeEvents.on(EVENT_TYPES.CONNECTION_STATUS, (data) => {
      setConnectionStatus(data.status);
      console.log('[LiveSupport] Connection status:', data.status);
    });

    // Watch for incoming escalations
    escalationWatcherRef.current = watchForEscalations((data, eventId) => {
      console.log('[LiveSupport] Received escalation:', data);

      // Create new conversation from escalation data
      const newConversation = {
        id: data.ticket_id,
        userId: data.client_id,
        userName: data.client_name,
        userAvatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${data.client_name}`,
        email: data.client_email,
        status: 'active',
        lastMessage: 'Escalated from chatbot - Waiting for agent response',
        lastMessageTime: new Date(data.created_at || Date.now()),
        createdAt: new Date(data.created_at || Date.now()),
        unreadCount: 1,
        isEscalated: true,
        escalatedFrom: 'chatbot',
        priority: data.priority || 'High',
      };

      // Add to conversations or update if exists
      setConversations((prev) => {
        const exists = prev.some((c) => c.id === data.ticket_id);
        if (exists) {
          return prev;
        }
        return [newConversation, ...prev];
      });

      // Mark notification badge
      setEscalationNotificationBadge((count) => count + 1);

      // Auto-select if it's the only conversation or first escalation
      setSelectedConversation((current) => {
        if (!current) {
          return newConversation;
        }
        return current;
      });

      // Store unread escalation
      setUnreadEscalations((prev) => [
        ...prev,
        {
          ticket_id: data.ticket_id,
          ...data,
          notificationId: eventId,
        },
      ]);
    });

    return () => {
      unsubConnection();
      if (escalationWatcherRef.current) {
        escalationWatcherRef.current();
      }
    };
  }, []);

  // Initial fetch and setup polling
  useEffect (() => {
    fetchConversations();

    // Set up polling to refresh conversations every 3 seconds
    pollIntervalRef.current = setInterval(() => {
      fetchConversations();
      if (selectedConversation) {
        fetchMessages(selectedConversation.id);
      }
    }, 3000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Refresh messages when selected conversation changes
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation?.id]);

  // Handle conversation selection
  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);

    // If this is an escalated conversation, mark it as read
    if (conversation.isEscalated && conversation.id) {
      markEscalationAsRead(conversation.id);

      // Reduce notification badge
      setEscalationNotificationBadge((count) => Math.max(0, count - 1));

      // Remove from unread escalations
      setUnreadEscalations((prev) =>
        prev.filter((e) => e.ticket_id !== conversation.id)
      );
    }

    // Fetch messages for this conversation
    fetchMessages(conversation.id);
  };

  // Handle sending message (admin response)
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !selectedConversation) return;

    try {
      const response = await fetch(`${API_BASE}/live-support/send-message.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticket_id: selectedConversation.id,
          message: messageInput,
          sender_type: 'admin',
          sender_id: 1, // TODO: Get actual admin ID from auth
          sender_name: 'Admin Support',
        }),
      });

      const data = await response.json();
      if (data.success) {
        setMessageInput('');
        // Refresh messages to show the new message
        fetchMessages(selectedConversation.id);
      }
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  // Handle conversation actions (pin, delete, block)
  const handleConversationAction = async (action) => {
    if (!selectedConversation) return;

    setActionLoading(true);
    try {
      const response = await fetch(`${API_BASE}/live-support/message-actions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: action,
          ticket_id: selectedConversation.id,
          actor_id: 1, // TODO: Get actual admin ID from auth
          actor_role: 'admin',
        }),
      });

      const data = await response.json();
      if (data.success) {
        // Show success message
        alert(`Conversation ${action}ed successfully`);
        
        // Close menu
        setShowActionMenu(false);

        // Refresh conversations list
        if (action === 'block' || action === 'delete') {
          fetchConversations();
          setSelectedConversation(null);
        }
      } else {
        alert(`Failed to ${action} conversation: ${data.message}`);
      }
    } catch (err) {
      console.error(`Error performing ${action} action:`, err);
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle click outside menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowActionMenu(false);
      }
    };

    if (showActionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showActionMenu]);

  // Filter conversations based on search
  const filteredConversations = conversations.filter((conv) =>
    conv.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (conv.email && conv.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const statusConfig = selectedConversation ? getStatusConfig(selectedConversation.status) : null;

  // Show loading state
  if (loading && conversations.length === 0) {
    return (
      <div className="flex h-screen bg-gray-50">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading Live Support...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <AdminSidebar />

      {/* Main Container */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <div className="border-b border-gray-200 bg-white px-6 py-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-gray-900">Live Support Center</h1>
            <div className="flex items-center gap-4">
              {/* Connection Status Indicator */}
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full transition-colors ${
                  connectionStatus === 'connected' ? 'bg-emerald-500' :
                  connectionStatus === 'disconnected' ? 'bg-red-500' :
                  'bg-yellow-500 animate-pulse'
                }`}></div>
                <span className="text-xs font-medium text-gray-600">
                  {connectionStatus === 'connected' ? 'Real-time Connected' :
                   connectionStatus === 'disconnected' ? 'Disconnected' :
                   'Connecting...'}
                </span>
              </div>

              {/* Escalation Notification Badge */}
              {escalationNotificationBadge > 0 && (
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-100 border border-red-300 rounded-full">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  <span className="text-xs font-semibold text-red-700">
                    {escalationNotificationBadge} New Escalation{escalationNotificationBadge !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-600">
            {conversations.length} active conversation{conversations.length !== 1 ? 's' : ''}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border-b border-red-200 px-6 py-3 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Content Area */}
        <div className="flex flex-1 overflow-hidden">
          {/* Conversations Sidebar */}
          <div className="w-80 border-r border-gray-200 bg-white flex flex-col">
            {/* Search bar */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Conversations List */}
            <div className="flex-1 overflow-y-auto">
              {filteredConversations.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <p className="text-sm">No conversations available</p>
                </div>
              ) : (
                filteredConversations.map((conversation) => {
                  const isSelected = selectedConversation?.id === conversation.id;
                  return (
                    <div
                      key={conversation.id}
                      onClick={() => handleSelectConversation(conversation)}
                      className={`p-4 border-b border-gray-100 cursor-pointer transition-all ${
                        isSelected
                          ? 'bg-purple-50 border-l-4 border-l-purple-600'
                          : 'hover:bg-gray-50'
                      } ${conversation.isEscalated ? 'bg-amber-50' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                      {/* Avatar with escalation indicator */}
                      <div className="relative">
                        <img
                          src={conversation.userAvatar}
                          alt={conversation.userName}
                          className="w-10 h-10 rounded-full flex-shrink-0"
                        />
                        {conversation.isEscalated && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white animate-pulse" title="Escalated from chatbot"></div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <p className="font-semibold text-sm text-gray-900 truncate">
                              {conversation.userName}
                            </p>
                            {conversation.isEscalated && (
                              <span className="px-2 py-0.5 text-xs font-bold bg-red-100 text-red-700 rounded whitespace-nowrap">
                                🤖 Escalated
                              </span>
                            )}
                          </div>
                          {conversation.unreadCount > 0 && (
                            <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-semibold leading-none text-white bg-red-500 rounded-full flex-shrink-0">
                              {conversation.unreadCount}
                            </span>
                          )}
                        </div>

                        {/* Status & Time */}
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex items-center gap-1.5">
                            <div
                              className={`w-2 h-2 rounded-full ${getStatusConfig(conversation.status).dotColor}`}
                            />
                            <span className="text-xs text-gray-600">
                              {getStatusConfig(conversation.status).label}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {getTimeAgo(conversation.lastMessageTime)}
                          </span>
                          {conversation.priority && (
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                              conversation.priority === 'High' ? 'bg-red-100 text-red-700' :
                              conversation.priority === 'Medium' ? 'bg-amber-100 text-amber-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {conversation.priority}
                            </span>
                          )}
                        </div>

                        {/* Last message preview */}
                        <p className="text-xs text-gray-600 truncate">
                          {conversation.lastMessage}
                        </p>
                      </div>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Chat Panel */}
          {selectedConversation ? (
            <div className="flex-1 flex flex-col">
              {/* Chat Header */}
              <div className="border-b border-gray-200 bg-white px-6 py-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src={selectedConversation.userAvatar}
                      alt={selectedConversation.userName}
                      className="w-12 h-12 rounded-full"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">
                        {selectedConversation.userName}
                      </p>
                      <p className="text-sm text-gray-600">
                        {selectedConversation.email}
                      </p>
                    </div>
                  </div>

                  {/* Status Badge and Menu */}
                  <div className="flex items-center gap-3">
                    {statusConfig && (
                      <div className={`px-3 py-1 rounded-full text-sm font-semibold ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                        {statusConfig.label}
                      </div>
                    )}

                    {/* Action Menu Button */}
                    <div className="relative" ref={menuRef}>
                      <button
                        onClick={() => setShowActionMenu(!showActionMenu)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Conversation actions"
                      >
                        <MoreVertIcon />
                      </button>

                      {/* Dropdown Menu */}
                      {showActionMenu && (
                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
                          <button
                            onClick={() => handleConversationAction('delete')}
                            disabled={actionLoading}
                            className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <TrashIcon />
                            <span>Delete Conversation</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <p>No messages yet</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isAdmin = msg.sender === 'admin';
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isAdmin ? 'justify-end' : 'justify-start'} animate-fadeIn`}
                      >
                        <div
                          className={`max-w-xs lg:max-w-md px-4 py-3 rounded-lg ${
                            isAdmin
                              ? 'bg-purple-600 text-white rounded-br-none'
                              : 'bg-gray-100 text-gray-900 rounded-bl-none'
                          }`}
                        >
                          <p className="text-sm">{msg.message}</p>
                          <p
                            className={`text-xs mt-2 ${
                              isAdmin ? 'text-purple-100' : 'text-gray-600'
                            }`}
                          >
                            {msg.timestamp.toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="border-t border-gray-200 bg-white px-6 py-4 shadow-lg">
                <form onSubmit={handleSendMessage} className="flex gap-3">
                  <input
                    type="text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    type="submit"
                    disabled={!messageInput.trim()}
                    className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    <SendIcon />
                    Send
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <p>Select a conversation to start chatting</p>
            </div>
          )}
        </div>
      </div>

      {/* Tailwind animation */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default LiveSupport;
